import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { ApiFeil } from '../api/klient';
import { filApi, type Forslag } from '../api/filer';
import { reiseApi, type Fremdrift, type GjeldendeSvar, type Meg, type Neste, type Sporsmal, type Sprak, type SvarInn, type SvarResultat, type TolkInn, type Tolkning } from '../api/reise';
import FremdriftListe from '../components/reise/FremdriftListe';
import Innlogging from '../components/reise/Innlogging';
import Invitasjon from '../components/reise/Invitasjon';
import DokumentasjonSide from '../components/reise/DokumentasjonSide';
import ForslagKort from '../components/reise/ForslagKort';
import SammendragSide from '../components/reise/SammendragSide';
import SporOssPanel from '../components/reise/SporOssPanel';
import SporsmalSkjema from '../components/reise/SporsmalSkjema';
import SvarOversikt from '../components/reise/SvarOversikt';
import TolkningPanel from '../components/reise/TolkningPanel';
import i18n, { harLagretSprak, SPRAK } from '../i18n';

type Fase = { navn: 'laster' } | { navn: 'feil'; kode: string } | { navn: 'innlogging' } | { navn: 'reise' } | { navn: 'utlogget' };

/**
 * /r/:lenke. Kundereisen: lenken sjekkes, kunden logger inn med engangskode, og svarer på ett spørsmål om
 * gangen. Lenken er en hemmelighet, og vises aldri på siden.
 */
export default function KundeReise() {
  const { t } = useTranslation();
  const { lenke = '' } = useParams();
  const [fase, setFase] = useState<Fase>({ navn: 'laster' });
  const serversprak = useRef<Sprak | null>(null);

  useEffect(() => {
    let avbrutt = false;
    reiseApi
      .lenkestatus(lenke)
      .then((status) => {
        if (avbrutt) return;
        serversprak.current = status.sprak;
        // Reisens språk brukes, med mindre kunden har valgt et annet på denne enheten
        if (!harLagretSprak() && i18n.resolvedLanguage !== status.sprak) i18n.changeLanguage(status.sprak);
        setFase(status.status === 'gyldig' ? { navn: 'reise' } : { navn: 'innlogging' });
      })
      .catch((e) => !avbrutt && setFase({ navn: 'feil', kode: e instanceof ApiFeil ? e.kode : 'ukjent' }));
    return () => {
      avbrutt = true;
    };
  }, [lenke]);

  const utlogget = useCallback((kode: string) => {
    if (kode === 'pause') setFase({ navn: 'utlogget' });
    else setFase(kode === 'ingen_okt' ? { navn: 'innlogging' } : { navn: 'feil', kode });
  }, []);

  if (fase.navn === 'reise') return <Reisen serversprak={serversprak} onUtlogget={utlogget} />;

  // Før innlogging: ett rolig kort midt på siden
  return (
    <section className="mx-auto max-w-lg pt-4 sm:pt-10">
      <div className="kort p-8 sm:p-10">
        <h1 className="mb-3 text-[1.75rem] font-semibold leading-tight">{t('reise.tittel')}</h1>
        {fase.navn !== 'utlogget' && <p className="mb-8 text-slate-600">{t('reise.ingress')}</p>}

        {fase.navn === 'laster' && (
          <p role="status" className="text-slate-500">
            {t('reise.laster')}
          </p>
        )}
        {fase.navn === 'feil' && (
          <p role="alert" className="melding-feil">
            {t(`feil.${fase.kode}`, { defaultValue: t('feil.ukjent') })}
          </p>
        )}
        {fase.navn === 'utlogget' && (
          <p role="status" className="melding-info">
            {t('reise.pause.utlogget')}
          </p>
        )}
        {fase.navn === 'innlogging' && <Innlogging token={lenke} onInnlogget={() => setFase({ navn: 'reise' })} />}
      </div>
    </section>
  );
}

/** Feil som betyr at økten eller lenken ikke lenger gjelder. */
const OKTFEIL = ['ingen_okt', 'lenke_utlopt', 'lenke_trukket_tilbake', 'lenke_ukjent'];

type Visning = 'sporsmal' | 'svar' | 'dokumentasjon' | 'sammendrag' | 'inviter' | 'pause';

interface ReisenProps {
  serversprak: React.MutableRefObject<Sprak | null>;
  onUtlogget: (kode: string) => void;
}

/**
 * Spørsmålene, svarene, invitasjon og pause. Svarene lagres på serveren for hvert spørsmål, så kunden kan
 * fortsette senere, også på en annen enhet.
 */
function Reisen({ serversprak, onUtlogget }: ReisenProps) {
  const { t, i18n: oversetter } = useTranslation();
  const [visning, setVisning] = useState<Visning>('sporsmal');
  const [meg, setMeg] = useState<Meg | null>(null);
  const [sporsmal, setSporsmal] = useState<Sporsmal | null>(null);
  const [ferdig, setFerdig] = useState(false);
  const [fremdrift, setFremdrift] = useState<Fremdrift | null>(null);
  const [svarliste, setSvarliste] = useState<GjeldendeSvar[] | null>(null);
  const [konsekvenser, setKonsekvenser] = useState<SvarResultat['konsekvenser']>([]);
  const [tolkning, setTolkning] = useState<{ inn: TolkInn; data: Tolkning } | null>(null);
  const [forslag, setForslag] = useState<Forslag[]>([]);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  /** Modulen kunden har valgt i fremdriften (T104). Reisen holder seg i den til den er ferdig. */
  const valgtModul = useRef<string | null>(null);
  const [svarFilter, setSvarFilter] = useState<string | null>(null);

  /** Feil om økten eller lenken logger kunden ut. Gir om feilen var en slik feil. */
  const oktfeil = useCallback(
    (e: unknown) => {
      const kode = e instanceof ApiFeil ? e.kode : 'ukjent';
      if (!OKTFEIL.includes(kode) && !(e instanceof ApiFeil && e.status === 401)) return false;
      onUtlogget(kode === 'ukjent' ? 'ingen_okt' : kode);
      return true;
    },
    [onUtlogget]
  );

  const handterFeil = useCallback(
    (e: unknown) => {
      if (!oktfeil(e)) setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    },
    [oktfeil]
  );

  /** Viser det neste spørsmålet. Har kunden valgt en modul, er det neste i den modulen, til den er ferdig. */
  const visNeste = useCallback(async (neste: Neste) => {
    let n = neste;
    if (valgtModul.current && n.sporsmal?.modulId !== valgtModul.current) {
      n = await reiseApi.neste(valgtModul.current);
      if (n.sporsmal?.modulId !== valgtModul.current) valgtModul.current = null;
    }
    setSporsmal(n.sporsmal);
    setFerdig(n.ferdig);
  }, []);

  const hentNeste = useCallback(async () => {
    const [neste, status] = await Promise.all([reiseApi.neste(valgtModul.current), reiseApi.fremdrift()]);
    if (valgtModul.current && neste.sporsmal?.modulId !== valgtModul.current) valgtModul.current = null;
    setSporsmal(neste.sporsmal);
    setFerdig(neste.ferdig);
    setFremdrift(status);
  }, []);

  /** Forslagene fra dokumentene. De er et tillegg, så en feil her stopper ikke reisen. */
  const hentForslag = useCallback(() => {
    filApi
      .forslag()
      .then((liste) => setForslag(Array.isArray(liste) ? liste : []))
      .catch(() => setForslag([]));
  }, []);

  useEffect(() => {
    hentNeste().catch(handterFeil);
    reiseApi.meg().then(setMeg).catch(handterFeil);
    hentForslag();
  }, [hentNeste, handterFeil, hentForslag]);

  /** Kunden bruker forslaget fra et dokument. Det lagres som svaret, og reisen går videre som etter et vanlig svar. */
  async function bekreftForslag(f: Forslag) {
    setLagrer(true);
    setFeil(null);
    try {
      const resultat = await filApi.bekreftForslag(f);
      setKonsekvenser(resultat.konsekvenser);
      await visNeste(resultat.neste);
      setFremdrift(await reiseApi.fremdrift());
    } catch (e) {
      handterFeil(e);
    } finally {
      setLagrer(false);
      hentForslag();
    }
  }

  async function avvisForslag(f: Forslag) {
    setForslag((liste) => liste.filter((x) => x !== f));
    await filApi.avvisForslag(f).catch(handterFeil);
    hentForslag();
  }

  const forslagHer = sporsmal ? forslag.find((f) => f.sporsmalId === sporsmal.id && f.instans === sporsmal.instans) : undefined;

  // Bytter kunden språk, lagres valget på serveren, og det som vises, hentes på nytt på det nye språket
  const sprak = oversetter.resolvedLanguage as Sprak | undefined;
  useEffect(() => {
    if (!sprak || !SPRAK.includes(sprak) || sprak === serversprak.current) return;
    serversprak.current = sprak;
    reiseApi
      .settSprak(sprak)
      .then(async () => {
        const [status, oppdatert] = await Promise.all([
          reiseApi.fremdrift(),
          sporsmal ? reiseApi.sporsmal(sporsmal.id, sporsmal.instans) : Promise.resolve(null),
        ]);
        setFremdrift(status);
        if (oppdatert) setSporsmal(oppdatert);
        if (svarliste) setSvarliste(await reiseApi.svarListe());
      })
      .catch(handterFeil);
    // Bare språket skal utløse dette
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprak]);

  async function vis(ny: Visning, filter: string | null = null) {
    setFeil(null);
    setVisning(ny);
    setSvarFilter(filter);
    if (ny === 'svar') reiseApi.svarListe().then(setSvarliste).catch(handterFeil);
    if (ny === 'sporsmal') hentForslag();
  }

  /** «Annet» med egne ord på et valgspørsmål går til intervjuassistenten, som foreslår et svar. */
  function skalTolkes(inn: SvarInn) {
    const valgt = inn.verdi === 'annet' || (Array.isArray(inn.verdi) && (inn.verdi as unknown[]).includes('annet'));
    return !!sporsmal && !!inn.kundensOrd && valgt && ['ett_valg', 'flere_valg', 'sjekkliste'].includes(sporsmal.type);
  }

  async function tolk(inn: TolkInn): Promise<boolean> {
    setLagrer(true);
    setFeil(null);
    try {
      setTolkning({ inn, data: await reiseApi.tolk(inn) });
      setFremdrift(await reiseApi.fremdrift());
      return true;
    } catch (e) {
      handterFeil(e);
      return false;
    } finally {
      setLagrer(false);
    }
  }

  async function bekreftTolkning() {
    if (!tolkning) return;
    setLagrer(true);
    setFeil(null);
    try {
      const resultat = await reiseApi.bekreftTolkning(tolkning.inn.sporsmalId, tolkning.inn.instans);
      setTolkning(null);
      setKonsekvenser(resultat.konsekvenser);
      await visNeste(resultat.neste);
      setFremdrift(await reiseApi.fremdrift());
    } catch (e) {
      handterFeil(e);
    } finally {
      setLagrer(false);
    }
  }

  async function endreTolkning() {
    if (!tolkning) return;
    try {
      setSporsmal(await reiseApi.sporsmal(tolkning.inn.sporsmalId, tolkning.inn.instans));
      setTolkning(null);
    } catch (e) {
      handterFeil(e);
    }
  }

  async function svar(inn: SvarInn): Promise<boolean> {
    if (skalTolkes(inn)) {
      return tolk({ sporsmalId: inn.sporsmalId, instans: inn.instans, verdi: inn.verdi ?? null, kundensOrd: inn.kundensOrd ?? '' });
    }
    setLagrer(true);
    setFeil(null);
    try {
      const resultat = await reiseApi.svar(inn);
      setKonsekvenser(resultat.konsekvenser);
      await visNeste(resultat.neste);
      setFremdrift(await reiseApi.fremdrift());
      return true;
    } catch (e) {
      if (e instanceof ApiFeil && e.kode === 'sporsmal_ikke_synlig') await hentNeste().catch(handterFeil);
      handterFeil(e);
      return false;
    } finally {
      setLagrer(false);
    }
  }

  async function tilbake() {
    setFeil(null);
    setKonsekvenser([]);
    try {
      setSporsmal(await reiseApi.tilbake(sporsmal ? { sporsmalId: sporsmal.id, instans: sporsmal.instans } : null));
      setFerdig(false);
    } catch (e) {
      handterFeil(e);
    }
  }

  /** Fra oversikten: åpner spørsmålet med svaret, så kunden kan endre det. */
  async function endre(sporsmalId: string, instans: string) {
    setFeil(null);
    setKonsekvenser([]);
    try {
      setSporsmal(await reiseApi.sporsmal(sporsmalId, instans));
      setFerdig(false);
      setVisning('sporsmal');
    } catch (e) {
      handterFeil(e);
    }
  }

  /**
   * Kunden velger en modul i fremdriften. En ferdig modul åpner svarene i den, så kunden kan se over og endre dem.
   * Ellers kommer det første ubesvarte spørsmålet i modulen.
   */
  async function velgModul(modul: Fremdrift['moduler'][number]) {
    if (modul.ferdig) {
      valgtModul.current = null;
      await vis('svar', modul.modulId);
      return;
    }
    valgtModul.current = modul.modulId;
    setTolkning(null);
    setKonsekvenser([]);
    setFeil(null);
    await hentNeste().catch(handterFeil);
  }

  async function loggUt() {
    try {
      await reiseApi.loggUt();
    } finally {
      onUtlogget('pause');
    }
  }

  const lagtBort = konsekvenser.filter((k) => k.status === 'gjelder_ikke_lenger').length;
  const tilBekreftelse = konsekvenser.filter((k) => k.status === 'bekreft_pa_nytt').length;
  const menyvalg: Visning[] = meg?.erEier
    ? ['sporsmal', 'svar', 'dokumentasjon', 'sammendrag', 'inviter', 'pause']
    : ['sporsmal', 'svar', 'dokumentasjon', 'sammendrag', 'pause'];
  const menynavn: Record<Visning, string> = {
    sporsmal: t('reise.meny.sporsmal'),
    svar: t('reise.meny.svar'),
    dokumentasjon: t('reise.meny.dokumentasjon'),
    sammendrag: t('reise.meny.sammendrag'),
    inviter: t('reise.meny.inviter'),
    pause: t('reise.meny.pause'),
  };

  return (
    <div>
      <header className="mb-8">
        {meg?.kundenavn && <p className="etikett mb-2">{meg.kundenavn}</p>}
        <h1 className="mb-6 text-[2rem] font-semibold leading-tight">{t('reise.tittel')}</h1>
        <nav aria-label={t('reise.meny.navn')} className="flex flex-wrap gap-x-7 border-b border-slate-200">
          {menyvalg.map((v) => (
            <button
              key={v}
              type="button"
              aria-current={visning === v ? 'page' : undefined}
              onClick={() => vis(v)}
              className={'fane -mb-px ' + (visning === v ? 'fane-aktiv' : '')}
            >
              {menynavn[v]}
            </button>
          ))}
        </nav>
      </header>

      {visning === 'sporsmal' && (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="min-w-0">
            {(lagtBort > 0 || tilBekreftelse > 0) && (
              <div role="status" className="mb-4 melding-advarsel">
                {lagtBort > 0 && <p>{t('reise.konsekvenser.lagtBort', { antall: lagtBort })}</p>}
                {tilBekreftelse > 0 && <p>{t('reise.konsekvenser.bekreft', { antall: tilBekreftelse })}</p>}
              </div>
            )}
            {sporsmal && tolkning && (
              <TolkningPanel
                sporsmal={sporsmal}
                tolkning={tolkning.data}
                lagrer={lagrer}
                feil={feil}
                onBekreft={bekreftTolkning}
                onOppfolging={(oppfolging) => tolk({ ...tolkning.inn, oppfolging })}
                onEndre={endreTolkning}
                onFortsett={() => {
                  setTolkning(null);
                  hentNeste().catch(handterFeil);
                }}
              />
            )}
            {sporsmal && !tolkning && forslagHer && (
              <div className="mb-4">
                <ForslagKort forslag={forslagHer} lagrer={lagrer} onBekreft={bekreftForslag} onAvvis={avvisForslag} />
              </div>
            )}
            {sporsmal && !tolkning && (
              <div className="kort p-6 sm:p-10">
                <SporsmalSkjema
                  key={`${sporsmal.id}|${sporsmal.instans}`}
                  sporsmal={sporsmal}
                  lagrer={lagrer}
                  feil={feil}
                  kanGaTilbake={(fremdrift?.besvart ?? 0) > 0}
                  onSvar={svar}
                  onTilbake={tilbake}
                />
              </div>
            )}
            {!sporsmal && ferdig && (
              <div className="kort p-6 sm:p-10">
                <span aria-hidden="true" className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-aksent-50 text-aksent-700">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <h2 className="mb-2 text-[1.625rem] font-semibold">{t('reise.ferdig.tittel')}</h2>
                <p className="mb-8 max-w-lesbar text-slate-600">{t('reise.ferdig.ingress')}</p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button type="button" onClick={() => vis('sammendrag')} className="knapp-primaer">
                    {t('reise.meny.sammendrag')}
                  </button>
                  <button type="button" onClick={tilbake} className="knapp-sekundaer">
                    {t('reise.sporsmal.tilbake')}
                  </button>
                </div>
              </div>
            )}
            {!sporsmal && !ferdig && feil && (
              <p role="alert" className="melding-feil">
                {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
              </p>
            )}
          </div>
          <aside className="flex flex-col gap-4 lg:sticky lg:top-24">
            {fremdrift && <FremdriftListe fremdrift={fremdrift} aktivModul={sporsmal?.modulId ?? null} onVelg={velgModul} />}
            <SporOssPanel
              sporsmal={sporsmal}
              onFeil={oktfeil}
              onBekreftEndring={(endring) => svar({ sporsmalId: endring.sporsmalId, instans: endring.instans, verdi: endring.verdi })}
            />
          </aside>
        </div>
      )}

      <div className="max-w-4xl">
        {visning === 'svar' && svarliste && <SvarOversikt svar={svarliste} onEndre={endre} modul={svarFilter} onVisAlle={() => setSvarFilter(null)} />}

        {visning === 'dokumentasjon' && <DokumentasjonSide onFeil={oktfeil} />}

        {visning === 'sammendrag' && (
          <SammendragSide onEndreSvar={endre} onFortsett={() => vis('sporsmal').then(() => hentNeste().catch(handterFeil))} onFeil={handterFeil} />
        )}

        {visning === 'inviter' && fremdrift && <Invitasjon moduler={fremdrift.moduler} />}
      </div>

      {visning === 'pause' && (
        <section aria-labelledby="pause-tittel" className="kort max-w-2xl p-6 sm:p-10">
          <h2 id="pause-tittel" className="mb-2 text-[1.625rem] font-semibold">
            {t('reise.pause.tittel')}
          </h2>
          <p className="mb-8 text-slate-600">{t('reise.pause.tekst')}</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => vis('sporsmal')} className="knapp-primaer">
              {t('reise.pause.fortsett')}
            </button>
            <button type="button" onClick={loggUt} className="knapp-sekundaer">
              {t('reise.pause.loggUt')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
